/**
 * Calculator Draft Hook
 * Syncs calculator state with React Query-based draft persistence
 */

import { useEffect, useRef, useCallback } from 'react';
import { useDraftQuery } from './useDraftQuery';
import { useCalculatorLogger } from '@/hooks/calculator/useCalculatorLogger';
import type { MortgageData } from '@/components/calculator/CalculatorTabs';
import type { DraftLoadResponse } from '@/types/drafts';

interface UseCalculatorDraftProps {
  mortgageData: MortgageData;
  setMortgageData: (data: MortgageData | ((prev: MortgageData) => MortgageData)) => void;
  step?: string;
}

export function useCalculatorDraft({ 
  mortgageData, 
  setMortgageData, 
  step = 'basic' 
}: UseCalculatorDraftProps) {
  const logger = useCalculatorLogger('CalculatorDraft');
  
  // Guards to prevent state loops
  const hasHydrated = useRef(false);
  const lastSavedLoanType = useRef<string | null>(null);
  const isHydrating = useRef(false);
  
  // Hydration callback - runs once when draft is loaded
  const handleHydrate = useCallback((draft: DraftLoadResponse) => {
    if (hasHydrated.current || isHydrating.current) return;
    
    const startTime = performance.now();
    isHydrating.current = true;
    
    const values = draft.answers;
    if (!values || Object.keys(values).length === 0) {
      hasHydrated.current = true;
      isHydrating.current = false;
      return;
    }
    
    // Convert draft values back to MortgageData format
    const draftMortgageData: Partial<MortgageData> = {};
    
    // Map common fields
    if (values.homePrice) draftMortgageData.homePrice = values.homePrice;
    if (values.downPayment) draftMortgageData.downPayment = values.downPayment;
    if (values.downPaymentPercentage) draftMortgageData.downPaymentPercentage = values.downPaymentPercentage;
    if (values.interestRate) draftMortgageData.interestRate = values.interestRate;
    if (values.loanTerm) draftMortgageData.loanTerm = values.loanTerm;
    if (values.loanType) draftMortgageData.loanType = values.loanType;
    if (values.zipCode) draftMortgageData.zipCode = values.zipCode;
    if (values.state) draftMortgageData.state = values.state;
    if (values.city) draftMortgageData.city = values.city;
    if (values.creditScore) draftMortgageData.creditScore = values.creditScore;
    if (values.propertyType) draftMortgageData.propertyType = values.propertyType;
    if (values.isVeteran !== undefined) draftMortgageData.isVeteran = values.isVeteran;
    if (values.vaStatus) draftMortgageData.vaStatus = values.vaStatus;
    if (values.vaDisabilityStatus) draftMortgageData.vaDisabilityStatus = values.vaDisabilityStatus;
    
    // Store the loan type we're hydrating with
    lastSavedLoanType.current = values.loanType || null;
    
    // Apply draft data to calculator
    setMortgageData(prev => ({ ...prev, ...draftMortgageData }));
    
    const fieldCount = Object.keys(draftMortgageData).length;
    logger.logHydration('draft', fieldCount, startTime);
    
    // Mark hydration complete after a tick to allow state to settle
    setTimeout(() => {
      hasHydrated.current = true;
      isHydrating.current = false;
    }, 100);
  }, [setMortgageData, logger]);

  // Use React Query for draft persistence
  const {
    isLoading,
    isSaving,
    lastSaved,
    saveDraft,
    clearDraft,
  } = useDraftQuery({
    formSlug: 'mortgage_calculator',
    step,
    onHydrate: handleHydrate,
  });

  // Save calculator state to draft when it changes - ONLY AFTER HYDRATION
  useEffect(() => {
    // Skip if still loading or hydrating
    if (isLoading || !hasHydrated.current || isHydrating.current) {
      return;
    }

    const startTime = performance.now();
    
    const draftValues = {
      homePrice: mortgageData.homePrice,
      downPayment: mortgageData.downPayment,
      downPaymentPercentage: mortgageData.downPaymentPercentage,
      interestRate: mortgageData.interestRate,
      loanTerm: mortgageData.loanTerm,
      loanType: mortgageData.loanType,
      zipCode: mortgageData.zipCode,
      state: mortgageData.state,
      city: mortgageData.city,
      creditScore: mortgageData.creditScore,
      propertyType: mortgageData.propertyType,
      isVeteran: mortgageData.isVeteran,
      vaStatus: mortgageData.vaStatus,
      vaDisabilityStatus: mortgageData.vaDisabilityStatus,
    };

    // Skip save if loan type hasn't changed (prevents loop on initial mount)
    if (mortgageData.loanType === lastSavedLoanType.current) {
      // Still save other fields via React Query (which handles deduplication)
      saveDraft(draftValues);
      return;
    }

    // Loan type changed - update ref and save
    logger.logChange('loanType', lastSavedLoanType.current, mortgageData.loanType, 'draftSave');
    lastSavedLoanType.current = mortgageData.loanType;
    
    saveDraft(draftValues);
    logger.logSave('draft', Object.keys(draftValues).length, startTime);
  }, [mortgageData, saveDraft, isLoading, logger]);

  return {
    isSaving,
    lastSaved,
    isLoading,
    clearDraft,
  };
}
